/**
 * FEAT-31 code replay — the pure half: the splice a recorder stores and the
 * timeline a player re-types. No DOM, no network (`codeReplayRecorder.ts`
 * owns the browser side, `CodeReplayPlayer.tsx` the recruiter's view).
 *
 * A replay is the EDIT HISTORY of the code editor, never a recording of the
 * screen. The recorder compares the editor's text with its last sample at
 * most every couple of seconds and stores ONE splice — where, how many units
 * went, what came in — plus a marker for a paste, a Run, a Reset or a
 * restore. Times are whole seconds: nothing here can reconstruct per-key
 * timing.
 *
 * Offsets and lengths are UTF-16 code units (`String.length`, Monaco's
 * offsets). The server counts the same way (`backend/files/replay.py`).
 */

/** `restore` (B5): the code kept in the tab across a reload, put back into
 *  the editor when it mounted — one step, never typing. */
export type ReplayStepKind = "paste" | "run" | "reset" | "restore";

export interface ReplayRunResult {
  /** Example tests passed … */
  p: number;
  /** … out of this many. */
  t: number;
}

export interface ReplayStep {
  /** Whole seconds since the chunk's `t0`. */
  dt: number;
  o: number;
  d: number;
  i: string;
  k?: ReplayStepKind | null;
  run?: ReplayRunResult | null;
}

export interface ReplayChunk {
  v: number;
  /** One editor mount — a reload starts a new one. */
  sid: string;
  /** The chunk's index within its `sid`. */
  n: number;
  /** Client clock, epoch seconds, when the chunk's first step was sampled. */
  t0: number;
  /** A keyframe: the editor text before the first step. null = continue. */
  replay_base?: string | null;
  base_len: number;
  /** A chunk before this one was dropped (always a keyframe). */
  gap?: boolean;
  replay_steps: ReplayStep[];
}

/** A chunk as the recruiter's read returns it. */
export interface StoredReplayChunk extends ReplayChunk {
  /** The attempt-wide upload order (server-minted). */
  seq: number;
  server_ms: number;
}

export interface Splice {
  o: number;
  d: number;
  i: string;
}

const isHighSurrogate = (code: number): boolean => code >= 0xd800 && code <= 0xdbff;
const isLowSurrogate = (code: number): boolean => code >= 0xdc00 && code <= 0xdfff;

/**
 * The ONE splice that turns `prev` into `next` (common prefix + common
 * suffix), or null when they are equal. Several far-apart edits inside one
 * sample (multi-cursor, a formatter, an undo) become one covering splice —
 * larger, never wrong.
 *
 * Snapped to code-point boundaries: a prefix or suffix cut between the two
 * halves of a surrogate pair would put a LONE surrogate in `i`, which cannot
 * be encoded as UTF-8 and which the server refuses.
 */
export function diffSplice(prev: string, next: string): Splice | null {
  if (prev === next) return null;
  const max = Math.min(prev.length, next.length);
  let start = 0;
  while (start < max && prev.charCodeAt(start) === next.charCodeAt(start)) start += 1;
  if (start > 0 && isHighSurrogate(prev.charCodeAt(start - 1))) start -= 1;
  let tail = 0;
  const maxTail = max - start;
  while (
    tail < maxTail &&
    prev.charCodeAt(prev.length - 1 - tail) === next.charCodeAt(next.length - 1 - tail)
  ) {
    tail += 1;
  }
  if (tail > 0 && isLowSurrogate(prev.charCodeAt(prev.length - tail))) tail -= 1;
  return {
    o: start,
    d: prev.length - tail - start,
    i: next.slice(start, next.length - tail),
  };
}

export function applyStep(text: string, step: Splice): string {
  return text.slice(0, step.o) + step.i + text.slice(step.o + step.d);
}

export type ReplayFrameKind =
  | "start"
  | "edit"
  | "paste"
  | "run"
  | "reset"
  | "restore"
  | "break";

export interface ReplayFrame {
  kind: ReplayFrameKind;
  /** The editor's text AFTER this frame. */
  text: string;
  /** Real seconds since the first frame (the candidate's clock). */
  at: number;
  /** Playback seconds since the first frame: `at` with idle gaps shortened. */
  playAt: number;
  /** What this frame inserted, as a range of `text` — the player's highlight. */
  inserted: { from: number; to: number } | null;
  /** How many units this frame removed. */
  removed: number;
  run: ReplayRunResult | null;
}

export interface ReplayTimeline {
  frames: ReplayFrame[];
  /** Real seconds from the first frame to the last. */
  durationS: number;
  /** Playback seconds from the first frame to the last. */
  playDurationS: number;
  pastes: number;
  runs: number;
  /** Places where the record does not join up: a reload that lost unsaved
   *  typing, or a chunk that never arrived. The player shows them. */
  breaks: number;
  /** Chunks that could not be applied (waiting for the next keyframe). */
  skippedChunks: number;
}

/** The longest pause playback sits through; a longer one is cut to this. */
export const REPLAY_MAX_IDLE_S = 3;
/** The shortest time playback gives one step. Times are whole seconds, so
 *  several steps can share one (typing, then a paste, then a Run): without a
 *  floor they would share a playback time too — the player could then never
 *  rest on, or step back to, any but the last of them. */
export const REPLAY_MIN_STEP_S = 0.5;

/**
 * Chunks → the frames a player steps through.
 *
 * Order is the server's (`seq`); a chunk uploaded twice is applied once
 * (`sid` + `n`). A keyframe always re-anchors the text — when it differs from
 * where the record stood, that is a BREAK, shown as one, never smoothed into
 * a fake edit. A chunk without a keyframe is applied only if it directly
 * continues the chunk before it; otherwise it (and what follows) is skipped
 * until the next keyframe, so one lost object never corrupts the rest.
 */
export function buildTimeline(chunks: StoredReplayChunk[]): ReplayTimeline {
  const seen = new Set<string>();
  const ordered = [...chunks]
    .sort((a, b) => a.seq - b.seq)
    .filter((chunk) => {
      const id = `${chunk.sid}:${chunk.n}`;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });

  const frames: ReplayFrame[] = [];
  let text: string | null = null;
  let last: { sid: string; n: number } | null = null;
  let synced = false;
  let origin: number | null = null;
  let at = 0;
  let playAt = 0;
  let pastes = 0;
  let runs = 0;
  let breaks = 0;
  let skippedChunks = 0;

  const push = (
    kind: ReplayFrameKind,
    nextText: string,
    clock: number,
    extra: Partial<Pick<ReplayFrame, "inserted" | "removed" | "run">> = {},
  ): void => {
    if (origin === null) origin = clock;
    // A client clock that jumps back must not run the replay backwards.
    const nextAt = Math.max(at, clock - origin);
    // Strictly increasing, so every frame has a playback moment of its own.
    playAt +=
      frames.length === 0
        ? 0
        : Math.max(REPLAY_MIN_STEP_S, Math.min(nextAt - at, REPLAY_MAX_IDLE_S));
    at = nextAt;
    frames.push({
      kind,
      text: nextText,
      at,
      playAt,
      inserted: extra.inserted ?? null,
      removed: extra.removed ?? 0,
      run: extra.run ?? null,
    });
  };

  for (const chunk of ordered) {
    const base = chunk.replay_base ?? null;
    if (base !== null) {
      if (text === null) {
        push("start", base, chunk.t0);
      } else if (base !== text) {
        breaks += 1;
        push("break", base, chunk.t0);
      }
      text = base;
      synced = true;
    } else {
      const continues: boolean =
        synced &&
        text !== null &&
        last !== null &&
        last.sid === chunk.sid &&
        last.n + 1 === chunk.n &&
        text.length === chunk.base_len;
      if (!continues) {
        synced = false;
        skippedChunks += 1;
        last = { sid: chunk.sid, n: chunk.n };
        continue;
      }
    }
    last = { sid: chunk.sid, n: chunk.n };
    for (const step of chunk.replay_steps) {
      if (text === null || step.o + step.d > text.length) {
        // A stored chunk the server validated cannot get here; a hand-edited
        // or truncated one stops applying rather than inventing text.
        synced = false;
        skippedChunks += 1;
        break;
      }
      const kind: ReplayFrameKind = step.k ?? "edit";
      const next = applyStep(text, step);
      if (kind === "paste") pastes += 1;
      if (kind === "run") runs += 1;
      push(kind, next, chunk.t0 + step.dt, {
        inserted: step.i.length > 0 ? { from: step.o, to: step.o + step.i.length } : null,
        removed: step.d,
        run: step.run ?? null,
      });
      text = next;
    }
  }

  return {
    frames,
    durationS: at,
    playDurationS: playAt,
    pastes,
    runs,
    breaks,
    skippedChunks,
  };
}

/**
 * Whether the replay ENDS on other code than the answer that was saved — the
 * one gap a keyframe can never reveal, because nothing comes after it. Either
 * the replay lost its tail (offline at the end, the last chunk refused after
 * /submit, the per-attempt cap) or the answer did (the time-up save always
 * arrives after the deadline and is refused). The player says so instead of
 * letting the extra code read as text that appeared without being typed.
 * Nothing to say without a saved answer, without frames, or when the read was
 * cut short on purpose (`truncated` has its own note).
 */
export function replayEndsApart(
  timeline: ReplayTimeline,
  finalCode: string | null | undefined,
  truncated: boolean,
): boolean {
  if (finalCode == null || truncated) return false;
  const last = timeline.frames.at(-1);
  return last !== undefined && last.text !== finalCode;
}

/** The last frame at or before playback second `playAt` (0 when none). */
export function frameIndexAt(frames: ReplayFrame[], playAt: number): number {
  let lo = 0;
  let hi = frames.length - 1;
  let found = 0;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (frames[mid].playAt <= playAt) {
      found = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return found;
}

/** `75` → `1:15`, `3700` → `1:01:40`. */
export function formatReplayClock(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}
